"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Scope = void 0;
exports.getScopes = getScopes;
exports.hasAnyOfScopes = hasAnyOfScopes;
exports.hasScope = hasScope;
exports.scopes = void 0;
exports.traverseScopes = traverseScopes;
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
/*

I've tried a few ways of doing this:

# Enums
Pros:
- Autocomplete
- Can mark as deprecated
- Can add JSDoc descriptions
Cons:
- Each item needs explicit assignment, leading to possible mismatch on key/value
- Can't add extra properties like description
- Can't mix with string literals (this might be an advantage, as it enforces usage that reveals deprecated items)

# Objects
Pros:
- Extendable with anything we want
- Autocomplete works
- Can mark as deprecated
- Can add JSDoc descriptions
- Helper functions could be made to work directly with them
Cons:
- Very verbose if we want to see deprecated items and descriptions
- Using Object.keys loses the description, deprecated

# Nested objects
Pros:
- Extendable with anything we want
- Autocomplete works
- Can mark as deprecated
- Can add JSDoc descriptions
- Similar look and feel to the defaultApi object
- Helper functions could be made to work directly with them
Cons:
- Very verbose if we want to see deprecated items and descriptions
- Using Object.keys loses the description, deprecated

# Nested object with instantiated classes
Pros:
- Everything from nested objects, plus it's less hacky

# Arrays
Pros:
- Easy to iterate over

Cons:
- No autocomplete
- No deprecation
- No JSDoc descriptions

# String union
Pros:
- Simple
- Autocomplete

Cons:
- No deprecation
- No JSDoc descriptions


*** The winner is: ***
# Nested objects with instantiated classes

*/

/** Represents a permission scope. */
class Scope {
  constructor(description, deprecated) {
    _defineProperty(this, "name", '');
    _defineProperty(this, "description", '');
    _defineProperty(this, "deprecated", false);
    this.description = description || '';
    this.deprecated = deprecated || false;
  }

  /**
   * Returns the string value of this Scope.
   * @example
   * scopes.dashboard.view.toString() // Returns 'dashboard.view'
   */
  toString() {
    return this.name;
  }
}
exports.Scope = Scope;
/**
 * Defines the permissions available to users.
 * 
 * @example
 * 
 * has_scope(req, scopes.dashboard.view) // Returns true if req.user has the dashboard.view scope
 * scopes.dashboard.view.toString() // Returns 'dashboard.view'
 * 
 */
const scopes = exports.scopes = {
  accounting: {
    chart_of_accounts: {
      /** Allows a user to edit the chart of accounts */
      edit: new Scope('Allows a user to edit the chart of accounts')
    },
    items: {
      /** Allows a user to edit items */
      edit: new Scope('Allows a user to edit items'),
      /** Allows a user to view items in the admin console. (Back-end requests to get items are not restricted by this scope.) */
      view: new Scope('Allows a user to view items')
    },
    sales_invoices: {
      /** Allows a user to edit sales invoices */
      edit: new Scope('Allows a user to edit sales invoices')
    },
    /**
     * Allows a user to edit the chart of accounts
     * @deprecated Use accounting.chart_of_accounts.edit instead
     */
    edit_chart_of_accounts: new Scope('Allows a user to edit the chart of accounts', true),
    /**
     * Allows a user to edit items
     * @deprecated Use accounting.items.edit instead
     */
    edit_items: new Scope('Allows a user to edit items', true),
    /**
     * Allows a user to edit sales invoices
     * @deprecated Use accounting.sales_invoices.edit instead
     */
    edit_sales_invoices: new Scope('Allows a user to edit sales invoices', true)
  },
  customer: {
    /** Allows a user to edit customers and companies */
    edit: new Scope('Allows a user to edit customers and companies'),
    feedback: {
      /** Allows a user to view customer feedback */
      view: new Scope('Allows a user to view customer feedback')
    }
  },
  dashboard: {
    /** Allows users to view the dashboard and related data */
    view: new Scope('Allows users to view the dashboard and related data')
  },
  /** Shows developer tools */
  developer: new Scope('Shows developer tools'),
  inventory: {
    /** Allows a user to edit inventory in ways that would affect the ledger */
    adjust: new Scope('Allows a user to edit inventory in ways that would affect the ledger')
  },
  material_sheet: {
    /** Allows a user to edit material sheets */
    edit: new Scope('Allows a user to edit material sheets')
  },
  order: {
    /** Allows a user to edit an order and its parts */
    edit: new Scope('Allows a user to edit an order and its parts'),
    /** Allows a user to ignore and override errors when editing an order and its parts */
    override_errors: new Scope('Allows a user to ignore and override errors when editing an order and its parts')
  },
  order_part: {
    /** Allows a user to move an order part to an arbitrary step in the production pipeline */
    move: new Scope('Allows a user to move an order part to an arbitrary step in the production pipeline')
  },
  shipment_label: {
    /** Allows a user to manually add a label to a shipment, rather than using a scanner */
    bypass_scanner: new Scope('Allows a user to manually add a label to a shipment, rather than using the scanner')
  },
  training_event: {
    edit: new Scope('Allows a user to manage edit training events'),
    view: {
      self: new Scope('Allows a user to view their own training events'),
      all: new Scope('Allows a user to view all training events')
    }
  },
  user: {
    /** Allows a user to edit another user (except for permissions) */
    edit: new Scope('Allows a user to edit another user (except for permissions)'),
    permissions: {
      /** Allows editing which permission groups a user is a member of */
      edit: new Scope('Allows editing which permission groups a user is a member of')
    },
    stats: {
      view: {
        /** Allows a user to view all user stats */
        all: new Scope('Allows a user to view all user stats')
      }
    }
  },
  permission_groups: {
    /** Allows a user to edit the scopes of a permission group */
    edit: new Scope('Allows a user to edit the scopes of a permission group')
  }
};

/**
 * Recursively set the name of each scope in the provided object.
 */
function addName(path, obj) {
  if (obj instanceof Scope) {
    obj.name = path;
  } else {
    for (let key in obj) {
      let childPath = path === '' ? key : `${path}.${key}`;
      let childObj = obj[key];
      addName(childPath, childObj);
    }
    obj.toString = () => path;
    Object.freeze(obj);
  }
}
addName('', scopes);

/**
 * Traverses an object and calls the given callback for each scope.
 * @param scope The object to traverse.
 * @param callback The callback to call for each scope.
 */
function traverseScopes(scope, callback) {
  if (scope instanceof Scope) {
    callback(scope);
  } else {
    Object.values(scope).filter(v => typeof v === 'object').forEach(v => {
      traverseScopes(v, callback);
    });
  }
}

/**
 * Get an array of scopes for the given user.
 * @param user The user to get the scopes for.
 * @returns A string array of the user's scopes.
 */
function getScopes(user) {
  let userScopes = [];
  try {
    var _JSON$parse$scopes$sp, _JSON$parse$scopes, _user$permissions;
    userScopes = (_JSON$parse$scopes$sp = (_JSON$parse$scopes = JSON.parse((_user$permissions = user === null || user === void 0 ? void 0 : user.permissions) !== null && _user$permissions !== void 0 ? _user$permissions : '{}').scopes) === null || _JSON$parse$scopes === void 0 ? void 0 : _JSON$parse$scopes.split(',').map(s => s.trim()).filter(s => s.length > 0)) !== null && _JSON$parse$scopes$sp !== void 0 ? _JSON$parse$scopes$sp : [];
  } catch (e) {}
  return userScopes;
}

/**
 * Returns true if the given user has the given scope.
 * @param user The user to check.
 * @param scope The scope to check for.
 * @returns True if the user has the scope.
 */
function hasScope(user, scope) {
  return getScopes(user).includes(scope.toString());
}

/**
 * Returns true if the given user has any of the given scopes.
 * @param user The user to check.
 * @param scopes The scopes to check for.
 * @returns True if the user has any of the scopes.
 */
function hasAnyOfScopes(user, scopes) {
  return scopes.some(scope => hasScope(user, scope));
}